import { ShieldCheck, BrainCircuit, ServerCog, Globe2, Users, Map, Stethoscope } from "lucide-react";

export const SERVICES = [
  {
    id: "aml",
    testId: "service-card-aml",
    icon: ShieldCheck,
    code: "01",
    title: "AML & KYC Compliance Audits",
    short: "High-security risk assessments and fraud detection systems for international banking, fintech, and premium operations.",
    long: "End-to-end Anti-Money Laundering and Know-Your-Customer programs engineered for regulated institutions. Independent audits, transaction monitoring calibration, sanctions screening, and fraud detection frameworks aligned with FATF, FinCEN, MAS, and EU AMLD standards.",
    capabilities: [
      "Independent AML/KYC audit & gap analysis",
      "Transaction monitoring rule tuning",
      "Sanctions & PEP screening architecture",
      "Fraud detection & typology modeling",
      "Regulatory reporting readiness (SAR/STR)",
    ],
  },
  {
    id: "ai",
    testId: "service-card-ai",
    icon: BrainCircuit,
    code: "02",
    title: "AI-Driven Enterprise Sales Engines",
    short: "Deploying autonomous, self-hosted AI outbound infrastructure to qualify leads, handle telesales, and sync with CRMs seamlessly.",
    long: "Self-hosted, tenant-isolated AI outbound systems that qualify accounts, run cadence execution, and hand off enriched opportunities directly into Salesforce, HubSpot or custom CRMs — with full observability and data residency control.",
    capabilities: [
      "Autonomous outbound + telesales agents",
      "Self-hosted LLM inference & guardrails",
      "Real-time lead qualification & routing",
      "Native CRM sync (Salesforce, HubSpot, Zoho)",
      "Full call logging & QA transcripts",
    ],
  },
  {
    id: "it",
    testId: "service-card-it",
    icon: ServerCog,
    code: "03",
    title: "Custom IT Services & Secure Software Engineering",
    short: "Building custom, multi-layered digital architectures and software optimized for enterprise compliance.",
    long: "Bespoke engineering for regulated enterprises — from zero-trust internal platforms to customer-facing applications. We architect for compliance first: SOC 2, ISO 27001, PCI-DSS, and GDPR alignment baked into the delivery lifecycle.",
    capabilities: [
      "Zero-trust internal platforms",
      "Compliance-aligned SDLC (SOC 2 / ISO 27001)",
      "Secure API & microservice design",
      "Cloud & on-prem hybrid deployment",
      "Legacy modernization with audit trails",
    ],
  },
  {
    id: "bps",
    testId: "service-card-bps",
    icon: Globe2,
    code: "04",
    title: "Next-Gen Business Process Services (BPS)",
    short: "Secure, scalable global backend operations spanning inbound, outbound and non-voice channels — built with maximum data privacy.",
    long: "Managed back-office and customer operations for high-trust industries. A full-stack BPS practice covering inbound support, outbound engagement and non-voice channels — delivered on tenant-isolated infrastructure with strict data segregation, role-based access, and full audit lineage for every touchpoint.",
    capabilities: [
      "Inbound contact center — L1/L2 voice support, tier escalation, 24/7 coverage",
      "Outbound engagement — telesales, appointment setting, retention & win-back",
      "Non-voice operations — email, live chat, ticketing, social & back-office queues",
      "Enterprise CRM operations, workflow orchestration & QA",
      "Data-privacy-first process design with multi-region residency controls",
      "SLA-backed reporting, QA scorecards & governance dashboards",
    ],
  },
  {
    id: "eor",
    testId: "service-card-eor",
    icon: Users,
    code: "05",
    title: "Employer of Record (EOR) Services",
    short: "Compliant global hiring, payroll and workforce administration — engage talent in 150+ jurisdictions without opening a local entity.",
    long: "A full Employer of Record practice that lets enterprises hire, pay and manage talent across borders with zero entity setup. We assume statutory employer responsibility while your team retains day-to-day operational control — with compliant contracts, benefits, tax withholding, and offboarding built into every jurisdiction we serve.",
    capabilities: [
      "Global employment & payroll in 150+ jurisdictions",
      "Contractor-to-full-time conversions with compliance review",
      "Compliant onboarding, benefits administration & tax withholding",
      "Visa & work-permit sponsorship support",
      "Termination & offboarding compliance with local labor law",
    ],
  },
  {
    id: "gis",
    testId: "service-card-gis",
    icon: Map,
    code: "06",
    title: "GIS Mapping & Geospatial Services",
    short: "Enterprise-grade geospatial production — cadastral, utility, LiDAR and remote-sensing pipelines built for governments, utilities, telecom and infrastructure operators.",
    long: "A dedicated GIS practice delivering high-precision geospatial data at scale. From cadastral digitization and utility network mapping to LiDAR classification, orthophoto processing and remote-sensing analytics — we operate on tenant-isolated, air-gap-capable infrastructure with strict provenance and audit lineage for every asset produced.",
    capabilities: [
      "Cadastral & land-parcel digitization from scanned records and satellite imagery",
      "Utility network mapping — electricity, water, gas, telecom (as-built and design)",
      "LiDAR processing — classification, DEM/DTM/DSM generation, feature extraction",
      "Orthophoto & photogrammetry pipelines — UAV, aerial, and satellite sources",
      "Remote sensing & change detection — multispectral, SAR and time-series analytics",
      "Topographic & contour mapping for infrastructure and urban planning",
      "Route, corridor & right-of-way surveys for transport and pipeline projects",
      "3D city models, BIM-GIS integration and digital-twin data preparation",
      "Base-map production, feature encoding & attribution to OGC / ISO 19100 standards",
      "QA/QC with dual-operator review, RMSE validation and delivery in Esri, QGIS, DGN, DWG, GeoJSON & KML formats",
    ],
  },
  {
    id: "rcm",
    testId: "service-card-rcm",
    icon: Stethoscope,
    code: "07",
    title: "Full-Cycle Revenue Cycle Management (RCM)",
    short: "End-to-end healthcare revenue cycle operations — from patient access and coding to denials, appeals and A/R recovery — delivered HIPAA-compliant, US-payer ready.",
    long: "A dedicated healthcare RCM practice that owns the entire revenue cycle for providers, hospitals, DME, RPM, and specialty groups. Every stage — front-office eligibility, mid-cycle coding, back-office claims and collections — runs on tenant-isolated, HIPAA-aligned infrastructure with certified coders, US-payer expertise and denial analytics that recover leakage other vendors miss.",
    capabilities: [
      "Patient scheduling, registration & demographic entry",
      "Insurance eligibility & benefits verification (real-time & batch)",
      "Prior authorization & pre-certification management",
      "Medical coding — ICD-10-CM, CPT, HCPCS, DRG (AAPC/AHIMA-certified coders)",
      "Charge capture, charge entry & charge audit",
      "Claims submission & scrubbing (837P/837I) with clearinghouse integration",
      "Payment posting — ERA/EOB reconciliation, patient payments, adjustments",
      "Denials management & appeals with root-cause analytics",
      "A/R follow-up & aged-bucket recovery (0–30, 31–60, 61–90, 90+)",
      "Patient billing, statements & collections with configurable dunning cycles",
      "Provider credentialing, enrollment & re-credentialing",
      "Underpayment recovery, contract variance & payer-mix analytics",
      "Compliance & audit — HIPAA, HITECH, PHI safeguards, NPI/NDC validation",
      "Real-time RCM dashboards — clean claim rate, days in A/R, denial rate, net collection %",
      "EHR/PM integration — Epic, Cerner, athenahealth, Kareo, eClinicalWorks, DrChrono, AdvancedMD",
    ],
  },
];
